import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import CreditPurchaseModal from '../payment/CreditPurchaseModal';
import { CREDIT_PACKS } from '../../hooks/useCreditCalculator';
import { Coins, ShoppingCart, TrendingUp, Sparkles } from 'lucide-react';

export default function CreditsSection() {
  const { userData } = useAuth();
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const credits = userData?.credits ?? 0;
  const totalSpent = userData?.totalSpent ?? 0;
  const totalPurchased = userData?.totalPurchased ?? 0;

  return (
    <div className="space-y-6">
      {/* Balance Card */}
      <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Coins className="w-5 h-5" />
          Credit Balance
        </h2>

        <div className="flex flex-col sm:flex-row sm:items-end gap-4 mb-6">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Available Credits</p>
            <p className="text-4xl font-bold text-emerald-600 dark:text-emerald-400">
              {credits.toLocaleString()}
            </p>
          </div>
          <button
            onClick={() => setShowPurchaseModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition font-medium text-sm"
          >
            <ShoppingCart className="w-4 h-4" />
            Buy Credits
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100 dark:border-dark-border">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Purchased</p>
            <p className="text-xl font-semibold text-gray-900 dark:text-white">
              {totalPurchased.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Spent</p>
            <p className="text-xl font-semibold text-gray-900 dark:text-white">
              R{totalSpent.toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      {/* Pricing Tiers */}
      <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Pricing Tiers
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Buy more credits to unlock better pricing.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {CREDIT_PACKS.map((pack) => (
            <div
              key={pack.id}
              className={`
                relative p-4 rounded-lg border-2 transition
                ${pack.popular
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                  : 'border-gray-200 dark:border-dark-border'
                }
              `}
            >
              {pack.popular && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-emerald-500 text-white text-xs font-medium rounded-full">
                  Popular
                </span>
              )}
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {pack.credits}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">credits</div>
              <div className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">
                R{pack.price}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                R{pack.pricePerCredit.toFixed(2)}/credit
              </div>
              {pack.savings > 0 && (
                <div className="mt-1 flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                  <Sparkles className="w-3 h-3" />
                  Save {pack.savings}%
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Purchase Modal */}
      <CreditPurchaseModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
      />
    </div>
  );
}
